import React from 'react';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { useSectionMotion, useMagnetico } from './motion';
import Resplandor from './Resplandor';
import Frase from './Frase';
import { useIdioma } from '../../i18n/idioma';

interface CierreProps {
    onPrimary: () => void;
    signedIn: boolean;
}

/**
 * Cierre de la página.
 *
 * Antes aquí había una franja de cinco tarjetas de confianza con su ícono en
 * caja redondeada, más un bloque de CTA centrado. Ahora la confianza es una
 * sola línea de texto —donde se lee como un hecho y no como publicidad— y todo
 * el peso visual se lo lleva la última frase.
 */
const Cierre: React.FC<CierreProps> = ({ onPrimary, signedIn }) => {
    const { t } = useIdioma();
    const boton = useMagnetico<HTMLButtonElement>();

    const scope = useSectionMotion<HTMLElement>(({ reveal, titular }) => {
        titular({ start: 'top 76%' });

        reveal('[data-fade]', { start: 'top 70%', stagger: 0.09 });
    });

    return (
        <section
            ref={scope}
            data-fondo="navy"
            className="relative overflow-hidden bg-navy-850 px-5 py-[clamp(5.5rem,12vw,10rem)] sm:px-8"
        >
            <Resplandor />

            <div className="relative mx-auto max-w-[1240px]">
                {/* «Juntos hasta la cima» en amarillo. Es el eslogan de la casa,
                    no una descripción, y es lo último que se lee: aquí el color
                    de marca dice algo. Hace pareja con el «Raza Pro» amarillo de
                    Reemplaza y cierra el arco de la página.

                    Sobre navy-850 da 8:1, y al ser `rp-h1` le bastaría con 3:1.

                    LA TENSIÓN, para quien venga después: este fold tiene el CTA
                    primario, que también es amarillo. Se sostiene porque son
                    escalas opuestas —un titular de hasta 88 px contra un botón
                    de 16— y el ojo no los confunde. Si alguna vez el titular
                    baja de tamaño, esto hay que revisarlo: dos amarillos del
                    mismo peso en un mismo fold sí pelean.

                    Volver atrás es una clase: `text-brand-400` → `text-white`. */}
                <h2 className="rp-h1 max-w-[13ch] text-brand-400">
                    <Frase marca="titular">{t('lp.cierre.titular')}</Frase>
                </h2>

                <p data-fade className="rp-lead mt-8 text-acero-300">
                    {t('lp.cierre.lead')}
                </p>

                {/* Un solo botón. Dos cajas del mismo tamaño una junto a otra
                    —una rellena y otra en contorno— es el par de CTA de todas las
                    landings; además reparte la mirada justo donde no hay que
                    repartirla. WhatsApp sigue estando, como enlace. */}
                <div
                    data-fade
                    className="mt-11 flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:gap-8"
                >
                    <button
                        ref={boton}
                        type="button"
                        onClick={onPrimary}
                        className="rp-btn rp-btn--primary w-full sm:w-auto"
                    >
                        {signedIn ? t('lp.cta.dashboard') : t('lp.cta.gratis')}
                        <ArrowRight className="h-[18px] w-[18px]" aria-hidden="true" />
                    </button>
                    <a
                        href="https://wa.me/14806700484"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rp-link text-white/80 hover:text-white"
                    >
                        <MessageCircle className="h-4 w-4" aria-hidden="true" />
                        {t('lp.cierre.whatsapp')}
                    </a>
                </div>

                {/* Antes: cuatro promesas separadas por puntos medios, que es
                    exactamente como las escribe todo el mundo. Dichas de corrido
                    se leen como algo que dice una persona. */}
                <p data-fade className="mt-10 max-w-[46ch] text-[15px] leading-relaxed text-acero-400">
                    {t('lp.cierre.nota')}
                </p>
            </div>
        </section>
    );
};

export default Cierre;
